import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Grid,
  Tooltip,
  Stack,
  Menu,
  MenuItem,
  Alert,
  Link,
  Chip,
} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import PersonIcon from '@mui/icons-material/Person';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import HandshakeIcon from '@mui/icons-material/Handshake';
import GroupIcon from '@mui/icons-material/Group';
import { t } from 'i18next';
import { updateManageByIdAction } from '../../../../actions/mangeActionV2';
import { showSuccessToast, showErrorToast } from '../../../../slices/toastSlice';
import { exportShipmentDetailPDF } from '../../../../util/exportUtils';
import SendEmailsDialog from './SendEmailsDialog';

export default function ConfirmShippingDocsDialog({ open, onClose, row, onSave }) {
  const dispatch = useDispatch();
  const updateManage = useSelector((s) => s.manageV2.updateManage) || {};
  const currencySymbol = useSelector((state) => state?.settings?.settingsMap?.currencySymbol || '$');
  const [emailMenuAnchor, setEmailMenuAnchor] = useState(null);
  const [sendDialog, setSendDialog] = useState({ open: false, row: null, taskType: null });

  const summary = useMemo(() => {
    if (!row) return null;
    const totalContainers = row.containers?.length || 0;
    const totalVehicles = row.containers?.reduce((s, c) => s + (c.shipmentItems?.length || 0), 0) || 0;
    return {
      bookingNumber: row?.booking?.number,
      vesselName: row?.booking?.vesselName,
      voyageNumber: row?.booking?.voyageNumber,
      destination: row?.booking?.destination?.name,
      shippingLine: row?.booking?.shippingLine?.name,
      totalContainers,
      totalVehicles,
    };
  }, [row]);

  useEffect(() => {
    if (!open) return;
    if (updateManage.loading === 'succeeded') {
      dispatch(showSuccessToast(t('Shipping documents marked as sent')));
      if (onSave) onSave();
      onClose && onClose();
    } else if (updateManage.loading === 'failed') {
      dispatch(showErrorToast(updateManage.errorMessage || t('Failed to update shipment')));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateManage.loading]);

  if (!row) return null;

  // resolve primary contacts
  const shipmentMode = row?.shipmentMode || row?.type;
  const primaryClient = {
    id: row?.primaryClientId,
    name: row?.primaryClientName,
    company: row?.primaryClientCompany,
    email: row?.primaryClientEmail,
    ccEmail: row?.primaryClientCcEmail,
    phoneNumber: row?.primaryClientPhoneNumber,
    exporterId: row?.primaryClientExporterId,
    exporterName: row?.primaryClientExporterName,
  };
  const hasPrimaryClient = Boolean(primaryClient.id || primaryClient.name || primaryClient.company);
  const shipmentClients = Array.isArray(row?.shipmentClients) ? row.shipmentClients : [];
  const clientContacts = [
    ...(hasPrimaryClient ? [{ ...primaryClient, role: 'PRIMARY' }] : []),
    ...(shipmentMode === 'LCL' ? shipmentClients : []),
  ];
  const primaryClientContact = clientContacts[0] || null;
  const secondaryClientContacts = shipmentMode === 'LCL' ? clientContacts.slice(1) : [];
  const shippingLine = row?.booking?.shippingLine || {};
  const broker = row?.broker || row?.booking?.broker || {};

  const hasClientRecipient = clientContacts.some((client) => Boolean(client?.email));
  const hasShippingEmail = Boolean(shippingLine?.email);
  const hasBrokerEmail = Boolean(broker?.email);
  const shippingRecipientType = hasBrokerEmail ? 'broker' : hasShippingEmail ? 'shippingLine' : null;
  const shippingOptionAvailable = Boolean(shippingRecipientType);
  const anyRecipientAvailable = hasClientRecipient || shippingOptionAvailable;
  // 'Send to All' should include client and also broker/shipping line when available.
  // Enable when at least one recipient exists (client or shipping/broker)

  const handleConfirm = () => {
    if (!row) return;
    const booking = row.booking || {};
    if (!booking.id) {
      dispatch(showErrorToast(t('Failed to update: booking not found')));
      return;
    }
    const payload = {
      ...booking,
      shippingInstructionsSent: dayjs().format('YYYY-MM-DD'),
    };
    dispatch(updateManageByIdAction({ mode: 'booking', id: booking.id, data: payload }));
  };

  // mailto compose removed — sending will be done via server using SendEmailsDialog

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1.5,
            maxHeight: 'calc(100vh - 48px)',
            width: { md: 980 },
          },
        }}
      >
      <DialogTitle
        sx={{
          px: 2.5,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: 1, bgcolor: '#EAF4FB', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <MarkEmailReadIcon color="primary" fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1.2 }}>{t('Confirm Shipping Documents Sent')}</Typography>
            <Typography variant="caption" color="text.secondary">{summary?.bookingNumber || '-'}</Typography>
          </Box>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
          {shipmentMode && (
            <Chip size="small" label={shipmentMode} color="primary" variant="outlined" sx={{ fontWeight: 800 }} />
          )}
          <Typography variant="caption" color="text.secondary">{dayjs().format('YYYY-MM-DD')}</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ p: 2.5, bgcolor: '#F6F8FA' }}>
        {summary && (
          <Stack spacing={1.5}>
            {/* Prominent warnings at the top (animated) */}
            {!hasBrokerEmail && (
              <Stack spacing={1}>
                {!hasBrokerEmail && hasShippingEmail && (
                  <Alert
                    severity="warning"
                    variant="outlined"
                    sx={{
                      fontWeight: 700,
                      bgcolor: '#FFF8E1',
                      borderColor: '#FFCA28',
                    }}
                  >
                    {t('Broker email is not available — emails will be sent to the Shipping Line when you choose Shipping Line or Broker.')}
                  </Alert>
                )}

                {!hasBrokerEmail && !hasShippingEmail && (
                  <Alert
                    severity="error"
                    variant="outlined"
                    sx={{
                      fontWeight: 800,
                      bgcolor: '#FFEBEE',
                      borderColor: '#EF5350',
                    }}
                  >
                    {t('No broker email and no shipping-line email available — Shipping Line or Broker cannot be used.')}
                  </Alert>
                )}
              </Stack>
            )}

            <InfoCard
              booking={summary.bookingNumber}
              vessel={summary.vesselName}
              voyage={summary.voyageNumber}
              destination={summary.destination}
              shippingLine={summary.shippingLine}
              containers={summary.totalContainers}
              vehicles={summary.totalVehicles}
            />

            <Stack spacing={0.5}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{t('Recipients')}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t('Review the recipients before sending or confirming the shipping documents.')}
              </Typography>
            </Stack>

            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}>
                <ContactCard
                  title={t('Shipping Line')}
                  icon={<LocalShippingIcon sx={{ color: 'info.main' }} />}
                  data={{ name: shippingLine?.name, email: shippingLine?.email, ccEmail: shippingLine?.ccEmail, phone: shippingLine?.phoneNumber, address: [shippingLine?.street, shippingLine?.city, shippingLine?.country].filter(Boolean).join(', '), nif: shippingLine?.nif }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ContactCard
                  title={t('Broker')}
                  icon={<HandshakeIcon sx={{ color: 'success.main' }} />}
                  data={{ name: broker?.name, email: broker?.email, ccEmail: broker?.ccEmail, phone: broker?.phoneNumber, address: [broker?.street, broker?.city, broker?.country].filter(Boolean).join(', '), nif: broker?.nif }}
                />
              </Grid>

              <Grid item xs={12}>
                <ClientContactsPanel
                  primaryClient={primaryClientContact}
                  secondaryClients={secondaryClientContacts}
                  shipmentMode={shipmentMode}
                />
              </Grid>
            </Grid>

            <Typography variant="caption" color="text.secondary">
              {t('You are about to mark the shipping instructions as sent for this shipment. This action will store the current date and time.')}
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ gap: 1.25, px: 2.5, py: 1.25, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1 }}>{updateManage.loading === 'pending' && <LinearProgress sx={{ height: 4, borderRadius: 1 }} />}</Box>
        <Stack direction="row" spacing={1} alignItems="center">

            <span>
              <Button
                startIcon={<EmailOutlinedIcon />}
                onClick={(e) => setEmailMenuAnchor(e.currentTarget)}
                disabled={updateManage.loading === 'pending'}
              >
                {t('Send Email')}
              </Button>
              <Menu
                anchorEl={emailMenuAnchor}
                open={Boolean(emailMenuAnchor)}
                onClose={() => setEmailMenuAnchor(null)}
              >
                <MenuItem
                  onClick={() => { setSendDialog({ open: true, row, taskType: 'client' }); setEmailMenuAnchor(null); }}
                  disabled={!hasClientRecipient}
                >
                  <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="body2">{t('Send to Client')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {shipmentMode === 'LCL'
                        ? t('Sends to primary client and shipment clients')
                        : t('Sends to primary client')}
                    </Typography>
                  </Box>
                </MenuItem>

                <MenuItem
                  onClick={() => { setSendDialog({ open: true, row, taskType: 'company' }); setEmailMenuAnchor(null); }}
                  disabled={!shippingOptionAvailable}
                >
                  <LocalShippingIcon sx={{ mr: 1, color: 'info.main' }} />
                  <Box>
                    <Typography variant="body2">{t('Send to Shipping Line or Broker')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {shippingRecipientType === 'broker'
                        ? t('Sends to broker only')
                        : shippingRecipientType === 'shippingLine'
                          ? t('Sends to shipping line when broker email is not available')
                          : t('No broker or shipping-line email available')}
                    </Typography>
                  </Box>
                </MenuItem>

                <MenuItem
                  onClick={() => { setSendDialog({ open: true, row, taskType: 'all' }); setEmailMenuAnchor(null); }}
                  disabled={!anyRecipientAvailable}
                >
                  <GroupIcon sx={{ mr: 1, color: 'secondary.main' }} />
                  <Box>
                    <Typography variant="body2">{t('Send to All')}</Typography>
                    <Typography variant="caption" color="text.secondary">{t('Sends to client and to broker & shipping line when available')}</Typography>
                  </Box>
                </MenuItem>
              </Menu>
            </span>

          <Tooltip title={t('Download Shipping Instructions PDF With Consignee Dock')}>
            <span>
              <Button onClick={() => exportShipmentDetailPDF(row, 'shipment', { mode:  'download' },  currencySymbol ,true )  } disabled={updateManage.loading === 'pending'}>
                {t('Download PDF')}
              </Button>
            </span>
          </Tooltip>
          
          <Button onClick={handleConfirm} variant="contained" color="primary" disabled={updateManage.loading === 'pending'}>
            {t('Confirm Sent')}
          </Button>
        </Stack>
          <Button onClick={onClose} disabled={updateManage.loading === 'pending'}>{t('cancel')}</Button>
      </DialogActions>
      </Dialog>
      <SendEmailsDialog open={sendDialog.open} onClose={() => setSendDialog({ open: false, row: null, taskType: null })} row={sendDialog.row} taskType={sendDialog.taskType} />
    </>
  );
}

function InfoField({ label, value }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, color: '#546E7A' }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value ?? '-'}</Typography>
    </Box>
  );
}

function ClientContactsPanel({ primaryClient, secondaryClients = [], shipmentMode }) {
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', bgcolor: 'background.paper', height: '100%' }}>
      <Box sx={{ px: 1.5, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, bgcolor: '#F7FAFC', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 28, height: 28, borderRadius: 1, bgcolor: '#EAF4FB', display: 'grid', placeItems: 'center' }}>
            <PersonIcon sx={{ color: 'primary.main', fontSize: 18 }} />
          </Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{t('Clients')}</Typography>
        </Box>
        <Stack direction="row" spacing={0.5}>
          {shipmentMode && <Chip size="small" label={shipmentMode} variant="outlined" sx={{ height: 22, fontWeight: 700 }} />}
          {secondaryClients.length > 0 && (
            <Chip size="small" label={`${secondaryClients.length} ${t('Secondary')}`} color="secondary" variant="outlined" sx={{ height: 22, fontWeight: 700 }} />
          )}
        </Stack>
      </Box>

      {primaryClient ? (
        <Box sx={{ p: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
            <Chip size="small" label={t('Primary')} color="primary" sx={{ height: 22, fontWeight: 700 }} />
            <Typography variant="body2" sx={{ fontWeight: 800, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{primaryClient?.name || '-'}</Typography>
          </Stack>
          {primaryClient?.company && (
            <Typography variant="caption" color="text.secondary" display="block">{primaryClient.company}</Typography>
          )}
          <ContactLine email={primaryClient?.email} ccEmail={primaryClient?.ccEmail} phone={primaryClient?.phoneNumber} />
          {primaryClient?.exporterName && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {t('Exporter')}: {primaryClient.exporterName}
            </Typography>
          )}
        </Box>
      ) : (
        <Box sx={{ p: 1.25 }}>
          <Typography variant="body2" color="text.secondary">-</Typography>
        </Box>
      )}

      {secondaryClients.length > 0 && (
        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', bgcolor: '#FBFCFE' }}>
          <Typography variant="caption" sx={{ display: 'block', px: 1.5, pt: 1, pb: 0.25, fontWeight: 800, color: '#546E7A' }}>
            {t('Secondary Clients')}
          </Typography>
          <Stack spacing={0.75} sx={{ p: 1.5, pt: 0.75, maxHeight: 260, overflowY: 'auto' }}>
            {secondaryClients.map((client, index) => (
              <Box
                key={`${client?.role || 'SECONDARY'}-${client?.id || client?.name || index}`}
                sx={{
                  p: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  '&:hover': { borderColor: 'primary.light', bgcolor: '#FFFFFF' },
                }}
              >
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {client?.name || '-'}
                    </Typography>
                    {client?.company && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {client.company}
                      </Typography>
                    )}
                  </Box>
                  <Chip size="small" label={t(client?.role || 'SECONDARY')} variant="outlined" sx={{ height: 22, flexShrink: 0 }} />
                </Stack>
                <ContactLine email={client?.email} ccEmail={client?.ccEmail} phone={client?.phoneNumber} dense />
                {client?.exporterName && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                    {t('Exporter')}: {client.exporterName}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

function ContactLine({ email, ccEmail, phone, dense = false }) {
  return (
    <Stack spacing={0.25} sx={{ mt: dense ? 0.5 : 0.75 }}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ overflowWrap: 'anywhere' }}>
        {email ? <Link href={`mailto:${email}`}>{email}</Link> : 'Email:'}
      </Typography>
      
        <Typography variant="caption" color="text.secondary" display="block" sx={{ overflowWrap: 'anywhere' }}>
          CC: {ccEmail}
        </Typography>
      
      {phone && (
        <Typography variant="caption" color="text.secondary" display="block">
          {t('Phone')}: {phone}
        </Typography>
      )}
    </Stack>
  );
}

function ContactCard({ title, icon, data = {} }) {
  const { name, company, email, ccEmail, phone, address, nif, role, exporterName } = data || {};
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper', overflow: 'hidden' }}>
      <Box sx={{ px: 1.5, py: 1.25, display: 'flex', gap: 1, alignItems: 'center', bgcolor: '#F7FAFC', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ width: 28, height: 28, borderRadius: 1, bgcolor: '#EAF4FB', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
          {icon}
        </Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{title}</Typography>
      </Box>

      <Box sx={{ p: 1.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 800 }}>{name || '-'}</Typography>
        {company && (
          <Typography variant="caption" color="text.secondary" display="block">
            {company}
          </Typography>
        )}
        {role && (
          <Typography variant="caption" color="text.secondary" display="block">
            {t(role)}
          </Typography>
        )}
        <ContactLine email={email} ccEmail={ccEmail} phone={phone} />

        {address && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#546E7A' }}>{'Address'}</Typography>
            <Typography variant="body2">{address || '-'}</Typography>
          </Box>
        )}
        {nif && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#546E7A' }}>{'NIF'}</Typography>
            <Typography variant="body2">{nif}</Typography>
          </Box>
        )}

        {exporterName && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#546E7A' }}>{t('Exporter')}</Typography>
            <Typography variant="body2">{exporterName}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function InfoCard({ booking, vessel, voyage, destination, shippingLine, containers, vehicles }) {
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper', overflow: 'hidden' }}>
      <Box sx={{ px: 1.5, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#FFFFFF' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{t('Shipment Summary')}</Typography>
        <Typography variant="caption" color="text.secondary">{t('Generated')} {dayjs().format('YYYY-MM-DD')}</Typography>
      </Box>

      <Grid container spacing={1.25} sx={{ p: 1.5 }}>
        <Grid item xs={12} md={4}><InfoField label={t('Booking')} value={booking} /></Grid>
        <Grid item xs={12} md={4}><InfoField label={t('Vessel')} value={vessel} /></Grid>
        <Grid item xs={12} md={4}><InfoField label={t('Voyage')} value={voyage} /></Grid>
        <Grid item xs={12} md={4}><InfoField label={t('Destination')} value={destination} /></Grid>
        <Grid item xs={12} md={4}><InfoField label={t('Shipping Line')} value={shippingLine} /></Grid>
        <Grid item xs={12} md={2}><InfoField label={t('Containers')} value={containers} /></Grid>
        <Grid item xs={12} md={2}><InfoField label={t('Vehicles')} value={vehicles} /></Grid>
      </Grid>
    </Box>
  );
}
